import { Link } from "@tanstack/react-router"
import { Blocks, ChevronDown, CircleAlert, KeyRound, Plug, Search, ShieldAlert } from "lucide-react"
import { useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { McpServer } from "@/api/generated/McpServer"
import type { McpToolInfo } from "@/api/generated/McpToolInfo"
import { useMcpServers, useMcpTools } from "@/api/queries/mcp"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import type { AgentFormValues } from "@/features/studio/agent-form"
import { VirtualList } from "@/features/studio/virtual-list"
import { cn } from "@/lib/utils"

type Assignment = AgentFormValues["mcp_servers"][number]

/**
 * Badges describing an integration (built-in/system, transport, state). `showDisabled` is off
 * where a separate status column already says the integration is disabled.
 */
export function McpBadges({
  server,
  compact,
  showDisabled = true,
}: {
  server: McpServer
  compact?: boolean
  showDisabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <>
      {server.transport === "builtin" ? (
        <StatusBadge tone="accent">{t("studio.mcp.builtin")}</StatusBadge>
      ) : server.is_system ? (
        <StatusBadge tone="accent">{t("studio.mcp.system")}</StatusBadge>
      ) : (
        !compact && <StatusBadge>{server.transport === "stdio" ? "stdio" : "HTTP"}</StatusBadge>
      )}
      {showDisabled && !server.enabled && (
        <StatusBadge tone="warning">{t("studio.mcp.disabled")}</StatusBadge>
      )}
      {server.auth_type === "oauth" && server.oauth_status !== "authorized" && (
        <StatusBadge tone="warning">
          <KeyRound />
          {server.oauth_status === "expired" ? t("studio.mcp.oauth.expired") : t("studio.mcp.oauth.needed")}
        </StatusBadge>
      )}
    </>
  )
}

function ToolRow({
  tool,
  allowedByOrg,
  enabled,
  onToggle,
}: {
  tool: McpToolInfo
  allowedByOrg: boolean
  enabled: boolean
  onToggle: (on: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <Label
      className={cn(
        "hover:bg-muted/40 flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 font-normal leading-normal",
        !allowedByOrg && "cursor-not-allowed opacity-60",
      )}
    >
      <Switch
        size="sm"
        checked={enabled && allowedByOrg}
        disabled={!allowedByOrg}
        onCheckedChange={onToggle}
        className="mt-0.5"
        aria-label={tool.name}
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <code className="font-mono text-[0.8125rem]">{tool.name}</code>
          {tool.title && tool.title !== tool.name && (
            <span className="text-muted-foreground text-xs">{tool.title}</span>
          )}
          {tool.read_only && <StatusBadge tone="success">{t("studio.mcp.tools.readOnly")}</StatusBadge>}
          {tool.destructive && (
            <StatusBadge tone="danger">
              <ShieldAlert />
              {t("studio.mcp.tools.destructive")}
            </StatusBadge>
          )}
          {!allowedByOrg && <StatusBadge>{t("studio.agents.tools.notAllowed")}</StatusBadge>}
        </span>
        {tool.description && (
          <span className="text-muted-foreground mt-0.5 line-clamp-2 block text-xs">{tool.description}</span>
        )}
      </span>
    </Label>
  )
}

function ToolsPanel({
  server,
  assignment,
  onChange,
}: {
  server: McpServer
  assignment: Assignment
  onChange: (a: Assignment) => void
}) {
  const { t } = useTranslation()
  const tools = useMcpTools(server.id)
  const [q, setQ] = useState("")
  const allowlist = server.enabled_tools
  const isAllowed = (name: string) => !allowlist || allowlist.includes(name)
  const all = tools.data ?? []
  const allowed = all.filter((x) => isAllowed(x.name))
  const enabledCount = allowed.filter((x) => !assignment.disabled_tools.includes(x.name)).length
  const filtered = q.trim()
    ? all.filter((x) =>
        `${x.name} ${x.title ?? ""} ${x.description}`.toLowerCase().includes(q.trim().toLowerCase()),
      )
    : all

  if (tools.isPending)
    return (
      <div className="space-y-2 p-3">
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
      </div>
    )
  if (tools.isError)
    return (
      <ErrorState
        variant="plain"
        error={tools.error}
        title={t("studio.agents.tools.loadFailed")}
        onRetry={() => void tools.refetch()}
        className="py-6"
      />
    )
  if (all.length === 0)
    return <p className="text-muted-foreground p-4 text-center text-sm">{t("studio.agents.tools.noTools")}</p>

  const setAll = (on: boolean) =>
    onChange({
      ...assignment,
      disabled_tools: on ? [] : allowed.map((x) => x.name),
    })

  return (
    <div className="space-y-2 p-3 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="flex items-center gap-2 text-sm font-medium leading-normal">
          <Switch
            size="sm"
            checked={enabledCount === allowed.length && allowed.length > 0}
            onCheckedChange={setAll}
            aria-label={t("studio.agents.tools.all")}
          />
          {t("studio.agents.tools.all")}
        </Label>
        <span className="text-muted-foreground text-xs tabular-nums">
          {t("studio.agents.tools.enabledCount", { enabled: enabledCount, total: allowed.length })}
        </span>
        {all.length > 8 && (
          <InputGroup className="ml-auto h-7 w-full sm:w-56">
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("studio.agents.tools.search")}
              aria-label={t("studio.agents.tools.search")}
              className="text-xs"
            />
          </InputGroup>
        )}
      </div>
      {allowlist && allowlist.length < all.length && (
        <p className="text-muted-foreground text-xs">
          {t("studio.agents.tools.orgLimited", { count: all.length - allowed.length })}
        </p>
      )}
      <VirtualList
        items={filtered}
        getKey={(x) => x.name}
        maxHeight={380}
        className="-mx-1 px-1"
        aria-label={t("studio.agents.tools.listLabel", { name: server.name })}
        renderItem={(tool) => (
          <ToolRow
            tool={tool}
            allowedByOrg={isAllowed(tool.name)}
            enabled={!assignment.disabled_tools.includes(tool.name)}
            onToggle={(on) =>
              onChange({
                ...assignment,
                disabled_tools: on
                  ? assignment.disabled_tools.filter((n) => n !== tool.name)
                  : [...assignment.disabled_tools, tool.name],
              })
            }
          />
        )}
      />
    </div>
  )
}

function ServerCard({
  server,
  assignment,
  expanded,
  onExpand,
  onAssign,
  onChange,
}: {
  server: McpServer
  assignment: Assignment | undefined
  expanded: boolean
  onExpand: () => void
  onAssign: (on: boolean) => void
  onChange: (a: Assignment) => void
}) {
  const { t } = useTranslation()
  const assigned = !!assignment
  const Icon = server.transport === "builtin" ? Blocks : Plug
  const disabledCount = assignment?.disabled_tools.length ?? 0
  return (
    <div
      className={cn(
        "bg-card rounded-xl border transition-colors",
        assigned && "border-primary/30 bg-primary/[0.02]",
      )}
      data-testid={`mcp-card-${server.id}`}
    >
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <Switch
          checked={assigned}
          onCheckedChange={onAssign}
          className="mt-1"
          aria-label={t("studio.agents.tools.assign", { name: server.name })}
        />
        <div className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium">{server.name}</span>
            <McpBadges server={server} compact />
          </div>
          <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
            {server.description || server.url || t("studio.mcp.noDescription")}
          </p>
          {server.last_error && (
            <p className="text-destructive mt-1 flex items-center gap-1 text-xs">
              <CircleAlert className="size-3 shrink-0" />
              <span className="truncate">{server.last_error}</span>
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {server.tool_count != null && (
            <span className="text-muted-foreground hidden text-xs tabular-nums sm:inline">
              {assigned && disabledCount > 0
                ? t("studio.agents.tools.someDisabled", { count: disabledCount })
                : t("studio.mcp.toolCount", { count: server.tool_count })}
            </span>
          )}
          {assigned && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onExpand}
              aria-expanded={expanded}
              data-testid={`mcp-tools-toggle-${server.id}`}
            >
              {t("studio.agents.tools.configure")}
              <ChevronDown className={cn("transition-transform", expanded && "rotate-180")} />
            </Button>
          )}
        </div>
      </div>
      {assigned && expanded && assignment && (
        <div className="border-t">
          <ToolsPanel server={server} assignment={assignment} onChange={onChange} />
        </div>
      )}
    </div>
  )
}

export function ToolsTab() {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<AgentFormValues>()
  const assignments = useWatch({ control, name: "mcp_servers" })
  const servers = useMcpServers()
  const [q, setQ] = useState("")
  const [expanded, setExpanded] = useState<number | null>(null)

  const set = (next: Assignment[]) => setValue("mcp_servers", next, { shouldDirty: true })
  const byId = useMemo(() => new Map(assignments.map((a) => [a.mcp_server_id, a])), [assignments])

  const list = useMemo(() => {
    const all = servers.data ?? []
    const needle = q.trim().toLowerCase()
    const filtered = needle
      ? all.filter((s) => `${s.name} ${s.description} ${s.url}`.toLowerCase().includes(needle))
      : all
    return [...filtered].sort(
      (a, b) => Number(byId.has(b.id)) - Number(byId.has(a.id)) || a.name.localeCompare(b.name),
    )
  }, [servers.data, q, byId])

  // assignments to integrations that no longer exist (deleted elsewhere)
  const orphaned = assignments.filter(
    (a) => servers.data && !servers.data.some((s) => s.id === a.mcp_server_id),
  )

  return (
    <div className="grid max-w-4xl gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">{t("studio.agents.tools.title")}</h2>
          <p className="text-muted-foreground text-xs">{t("studio.agents.tools.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <InputGroup className="h-8 w-full sm:w-56">
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("studio.agents.tools.searchIntegrations")}
              aria-label={t("studio.agents.tools.searchIntegrations")}
            />
          </InputGroup>
          <Button
            type="button"
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<Link to="/studio/mcp" />}
          >
            {t("studio.agents.tools.manage")}
          </Button>
        </div>
      </div>

      {servers.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      ) : servers.isError ? (
        <ErrorState error={servers.error} onRetry={() => void servers.refetch()} />
      ) : (servers.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Plug}
          title={t("studio.agents.tools.empty.title")}
          description={t("studio.agents.tools.empty.description")}
          action={
            <Button size="sm" nativeButton={false} render={<Link to="/studio/mcp" />}>
              {t("studio.agents.tools.empty.action")}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-2.5">
          {list.map((s) => (
            <ServerCard
              key={s.id}
              server={s}
              assignment={byId.get(s.id)}
              expanded={expanded === s.id}
              onExpand={() => setExpanded((e) => (e === s.id ? null : s.id))}
              onAssign={(on) => {
                if (on) {
                  set([...assignments, { mcp_server_id: s.id, disabled_tools: [] }])
                  setExpanded(s.id)
                } else set(assignments.filter((a) => a.mcp_server_id !== s.id))
              }}
              onChange={(a) => set(assignments.map((x) => (x.mcp_server_id === a.mcp_server_id ? a : x)))}
            />
          ))}
          {list.length === 0 && (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {t("table.noResultsHint", { query: q })}
            </p>
          )}
          {orphaned.length > 0 && (
            <p className="text-muted-foreground text-xs">
              {t("studio.agents.tools.orphaned", { count: orphaned.length })}{" "}
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => set(assignments.filter((a) => !orphaned.includes(a)))}
              >
                {t("studio.agents.tools.removeOrphaned")}
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  )
}
